export const SCORE_CALIBRATION_VERSION = 2;

export const CALIBRATED_TARGETS = Object.freeze({
  re1: { star3: 12, star2: 16, minValidSeconds: 10 },
  re2: { star3: 13, star2: 17 },
  re3: { star3: 14, star2: 19 },
  re4: { star3: 16, star2: 21 },
  re5: { star3: 14, star2: 19 },
  as1: { star3: 11, star2: 15 },
  as2: { star3: 10, star2: 13 },
  as3: { star3: 13, star2: 17 },
  as4: { star3: 14, star2: 19 },
  as5: { star3: 14, star2: 19 },
  bl1: { star3: 13, star2: 17 },
  bl2: { star3: 13, star2: 17 },
  bl3: { star3: 13, star2: 17 },
  bl4: { star3: 15, star2: 20 },
  bl5: { star3: 16, star2: 21 },
  sa1: { star3: 10, star2: 13 },
  sa2: { star3: 13, star2: 17 },
  sa3: { star3: 12, star2: 16 },
  sa4: { star3: 15, star2: 20 },
  sa5: { star3: 16, star2: 21 },
  mi1: { star3: 13, star2: 17 },
  mi2: { star3: 14, star2: 19 },
  mi3: { star3: 15, star2: 20 },
  mi4: { star3: 16, star2: 21 },
  mi5: { star3: 17, star2: 23 },
  ne1: { star3: 13, star2: 17 },
  ne2: { star3: 13, star2: 17 },
  ne3: { star3: 14, star2: 19 },
  ne4: { star3: 16, star2: 21 },
  ne5: { star3: 16, star2: 21 },
  bo1: { star3: 17, star2: 23 },
  bo2: { star3: 16, star2: 21 },
  bo3: { star3: 17, star2: 23 },
  bo4: { star3: 19, star2: 25 },
  bo5: { star3: 20, star2: 26 },
  bo6: { star3: 19, star2: 25 },
  bo7: { star3: 21, star2: 28 },
  bo8: { star3: 22, star2: 29 },
  bo9: { star3: 23, star2: 30 },
  bo10: { star3: 23, star2: 30 },
});

export const DEVELOPER_BEST_TIMES = Object.freeze({
  ju1: 8.064,
  ju2: 11.992,
  ju3: 13.965,
  ju4: 15.407,
  ju5: 23.402,
  wa1: 4.993,
  wa2: 6.844,
  wa3: 8.66,
  wa4: 12.124,
  wa5: 13.138,
  re1: 12.151,
  re2: 13.828,
  re3: 14.52,
  re4: 16.385,
  re5: 14.568,
  as1: 11.314,
  as2: 10.654,
  as3: 13.202,
  as4: 14.142,
  as5: 14.962,
  bl1: 13.485,
  bl2: 13.768,
  bl3: 13.994,
  bl4: 15.928,
  bl5: 16.888,
  sa1: 10.201,
  sa2: 13.162,
  sa3: 12.451,
  sa4: 15.092,
  sa5: 16.112,
  mi1: 13.451,
  mi2: 14.062,
  mi3: 15.282,
  mi4: 16.422,
  mi5: 17.872,
  ne1: 13.262,
  ne2: 13.605,
  ne3: 14.695,
  ne4: 16.155,
  ne5: 16.165,
  bo1: 17.515,
  bo2: 16,
  bo3: 17.54,
  bo4: 19.028,
  bo5: 20.038,
  bo6: 19.96,
  bo7: 21.879,
  bo8: 22.6,
  bo9: 23.1,
  bo10: 23.2,
});

const DEVELOPER_BENCHMARK_ID = "orbitrush-dev-benchmark";

export function getCalibratedTarget(roadId) {
  return CALIBRATED_TARGETS[roadId] || null;
}

export function starsForSeconds(roadId, seconds) {
  const target = getCalibratedTarget(roadId);
  if (!target || !Number.isFinite(seconds)) return null;
  if (seconds <= target.star3) return 3;
  if (seconds <= target.star2) return 2;
  return 1;
}

export function withDeveloperBenchmark(roadId, entries = []) {
  const time = DEVELOPER_BEST_TIMES[roadId];
  const scores = Array.isArray(entries)
    ? entries.filter((entry) => entry?.playerId !== DEVELOPER_BENCHMARK_ID)
    : [];
  if (!Number.isFinite(time)) return scores;

  scores.push({
    playerId: DEVELOPER_BENCHMARK_ID,
    name: "DEV BEST",
    time,
    stars: starsForSeconds(roadId, time),
    developer: true,
  });
  return scores.sort(
    (left, right) =>
      Number(left.time) - Number(right.time) ||
      Number(Boolean(right.developer)) - Number(Boolean(left.developer)),
  );
}

export function migrateSaveScores(input) {
  if (!input || typeof input !== "object") {
    return { save: input, changed: false };
  }

  const save = {
    ...input,
    best: { ...(input.best || {}) },
    stars: { ...(input.stars || {}) },
    times: { ...(input.times || {}) },
  };
  let changed = false;

  for (const [roadId, target] of Object.entries(CALIBRATED_TARGETS)) {
    const minimumMs = Number(target.minValidSeconds || 1) * 1000;
    const storedTimes = Array.isArray(save.times[roadId])
      ? save.times[roadId]
      : [];
    const candidates = [Number(save.best[roadId]), ...storedTimes.map(Number)]
      .filter((time) => Number.isFinite(time) && time >= minimumMs)
      .sort((a, b) => a - b);

    if (!candidates.length) {
      if (
        save.best[roadId] !== undefined ||
        save.stars[roadId] !== undefined ||
        save.times[roadId] !== undefined
      ) {
        delete save.best[roadId];
        delete save.stars[roadId];
        delete save.times[roadId];
        changed = true;
      }
      continue;
    }

    const uniqueTimes = [...new Set(candidates)].slice(0, 5);
    const best = uniqueTimes[0];
    const stars = starsForSeconds(roadId, best / 1000);
    if (
      Number(save.best[roadId]) !== best ||
      Number(save.stars[roadId]) !== stars ||
      JSON.stringify(save.times[roadId] || []) !== JSON.stringify(uniqueTimes)
    ) {
      save.best[roadId] = best;
      save.stars[roadId] = stars;
      save.times[roadId] = uniqueTimes;
      changed = true;
    }
  }

  return { save, changed };
}
